import React, { useState, useRef } from 'react';
import {
  Stack,
  Autocomplete,
  Tooltip,
  TextField,
  CircularProgress,
  Popover,
  Typography,
  Button,
  Box
} from "@mui/material";

import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";

import useManageTrackRecord from "../../hooks/useManageTrackRecord";
import ActionButtons from "./ActionButtons";

import "../../styles/main.css";

export default function ManageTrackRecord({
  setTrackRecordJson,
  trackRecordJson,
  trackRecordSql,
  setViewDataGrid,
  appBusinessUnit,
  aggregate,
  setAggregate,
  selectedTrackRecord,
  setSelectedTrackRecord,
  selectedTrackRecordName,
  setSelectedTrackRecordName,
  trackRecordName, 
  setTrackRecordName,
  isCashflow,
  children,
}) {

  const [unsavedChangesAnchor, setUnsavedChangesAnchor] = useState(false);
  const [pendingAutocompleteValue, setPendingAutocompleteValue] = useState(false);
  const [trackRecordLoading, setTrackRecordLoading] = useState(true);

  const {
    savedTrackRecordsList,
    TRListOnSelectionChange,
    TRNameOnChange,
    TRNameReset,
    clearTRName,
    clearTRQuery,
    saveOnClick,
    addOnClick,
    editOnClick,
    cloneOnClick,
    cloneTrackRecord,
    deleteOnClick,
    error,
    helperText,
    waitForSave,
    changesPending,
    focused,
    color,
    searchHidden, 
    nameHidden, 
    toggleFields
  } = useManageTrackRecord(
    trackRecordJson, 
    trackRecordSql, 
    setTrackRecordJson, 
    appBusinessUnit, 
    aggregate, 
    setAggregate, 
    selectedTrackRecord, 
    setSelectedTrackRecord, 
    selectedTrackRecordName, 
    setSelectedTrackRecordName, 
    trackRecordName, 
    setTrackRecordName,
    isCashflow,
    setTrackRecordLoading
  );

  const autocompleteRef = useRef(null);

  const handleAutocompleteChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      setTrackRecordName(newValue);
      TRListOnSelectionChange(null);
      return;
    }
    if (changesPending()) {
      setPendingAutocompleteValue(newValue);
      setUnsavedChangesAnchor(autocompleteRef.current);
    } else {
      TRListOnSelectionChange(newValue);
    }
  };

  const sortedOptions = React.useMemo(() => {
    const list = Array.isArray(savedTrackRecordsList) ? savedTrackRecordsList : [];
    const currentUsername = sessionStorage.getItem("Username");
    return [...list].sort((a, b) => {
      const aIsMine = a.username === currentUsername;
      const bIsMine = b.username === currentUsername;
      
      if (aIsMine && !bIsMine) return -1;
      if (!aIsMine && bIsMine) return 1;
      
      return a.trackRecordName.localeCompare(b.trackRecordName);
    });
  }, [savedTrackRecordsList]);

  return (
    <Stack
      className="manage-track-record"
      data-testid="manage-track-record"
      spacing={1}
      direction="column"
      sx={{ width: 1 }}
    >
      <Stack direction="row" spacing={1} useFlexGap sx={{ width: 1, rowGap: 2 }} alignItems="flex-end" flexWrap="wrap">
      <Autocomplete
        freeSolo
        disableClearable
        sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
        size="small"
        loading={trackRecordLoading}
        value={selectedTrackRecordName}
        inputValue={trackRecordName}
        onInputChange={(event, value) => setTrackRecordName(value ?? '')}
        onChange={(event, value) => handleAutocompleteChange(event, value)}
        options={sortedOptions}
        isOptionEqualToValue={(option, value) => option?.trackRecordId === value?.trackRecordId}
        groupBy={(option) => {
          const currentUsername = sessionStorage.getItem("Username");
          return (option.username && option.username === currentUsername) ? "Created by Me" : "Created by Others";
        }}
        getOptionLabel={(option) => {
          return typeof option === 'string' ? option : (option?.trackRecordName ?? '');
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Track Record Name"
            placeholder="Select from the list or add new"
            helperText={error ? helperText : undefined}
            error={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  {waitForSave ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Tooltip title="Discard Changes">
                      <IconButton
                        data-testid="clear-button"
                        onClick={() => {
                          clearTRName();
                          toggleFields(false);
                        }}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              ),
            }}
            inputRef={autocompleteRef}
          />
        )}
        renderOption={(props, option, { inputValue }) => {
          const matches = match(option.trackRecordName, inputValue, {
            insideWords: true,
          });
          const parts = parse(option.trackRecordName, matches);
          return (
            <li {...props}>
              <div>
                {parts.map((part, index) => (
                  <span
                    key={index}
                    style={{
                      fontWeight: part.highlight ? 700 : 400,
                    }}
                  >
                    {part.text}
                  </span>
                ))}
              </div>
            </li>
          );
        }}
      />
      <Popover
        open={Boolean(unsavedChangesAnchor)}
        anchorEl={unsavedChangesAnchor}
        onClose={() => {
          setUnsavedChangesAnchor(null);
          setPendingAutocompleteValue(null);
        }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ '& .MuiPopover-paper': { minWidth: '200px' } }}
      >
        <Typography sx={{ p: 1, textAlign: 'center' }}>
          You have unsaved changes.
          <br />
          Are you sure you want to proceed without saving?
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', p: 1 }}>
          <Button
            variant="contained"
            onClick={() => {
              TRListOnSelectionChange(pendingAutocompleteValue);
              setPendingAutocompleteValue(null);
              setUnsavedChangesAnchor(null);
            }}
          >
            Confirm
          </Button>
          <Button
            onClick={() => {
              setUnsavedChangesAnchor(null);
              setPendingAutocompleteValue(null);
            }}
          >
            Cancel
          </Button>
        </Box>
      </Popover>
      {children}
      </Stack>
      <ActionButtons
        nameHidden={nameHidden}
        selectedTrackRecordName={selectedTrackRecordName}
        trackRecordName={trackRecordName}
        setTrackRecordName={setTrackRecordName}
        toggleFields={toggleFields}
        TRNameReset={TRNameReset}
        error={error}
        helperText={helperText}
        changesPending={changesPending}
        trackRecordLoading={trackRecordLoading}
        isOwner={selectedTrackRecordName?.username === sessionStorage.getItem("Username")}
        actions={{
          addOnClick,
          saveOnClick,
          cloneOnClick,
          cloneTrackRecord,
          deleteOnClick,
          clearTRQuery
        }}
      />
    </Stack>
  );
}